<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.platformSkills.bulkEdit.title', { count }) }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <div class="modal-body">
          <p class="hint">{{ $t('bo.platformSkills.bulkEdit.hint') }}</p>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.colType') }}</label>
              <select v-model="form.skill_type">
                <option value="">{{ $t('bo.platformSkills.bulkEdit.noChange') }}</option>
                <option value="technical">{{ $t('bo.platformSkills.skillType.technical') }}</option>
                <option value="transversal">{{ $t('bo.platformSkills.skillType.transversal') }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.colLevel') }}</label>
              <select v-model="form.level">
                <option value="">{{ $t('bo.platformSkills.bulkEdit.noChange') }}</option>
                <option value="L0">{{ $t('bo.platformSkills.skillLevel.L0') }}</option>
                <option value="L2">{{ $t('bo.platformSkills.skillLevel.L2') }}</option>
                <option value="L3">{{ $t('bo.platformSkills.skillLevel.L3') }}</option>
              </select>
            </div>
          </div>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.colKind') }}</label>
              <select v-model="form.atom_kind">
                <option value="">{{ $t('bo.platformSkills.bulkEdit.noChange') }}</option>
                <option value="skill">{{ $t('bo.platformSkills.skillKind.skill') }}</option>
                <option value="tool">{{ $t('bo.platformSkills.skillKind.tool') }}</option>
                <option value="certification">{{ $t('bo.platformSkills.skillKind.certification') }}</option>
                <option value="framework">{{ $t('bo.platformSkills.skillKind.framework') }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.bulkEdit.moveToGroup') }}</label>
              <select v-model="form.skill_group_id">
                <option value="">{{ $t('bo.platformSkills.bulkEdit.noChange') }}</option>
                <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
            </div>
          </div>

          <div v-if="nothingSelected" class="warning-msg">
            {{ $t('bo.platformSkills.bulkEdit.selectAtLeastOne') }}
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="saving || nothingSelected">
            {{ saving ? $t('bo.common.saving') : $t('bo.platformSkills.bulkEdit.applyTo', { count }) }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'

const { t } = useI18n()

const props = defineProps({
  ids: { type: Array, required: true },
  groups: { type: Array, required: true },
})

const emit = defineEmits(['close', 'updated'])

const count = computed(() => props.ids.length)

const form = ref({
  skill_type: '',
  level: '',
  atom_kind: '',
  skill_group_id: '',
})

const saving = ref(false)
const error = ref('')

const nothingSelected = computed(() =>
  !form.value.skill_type && !form.value.level && !form.value.atom_kind && !form.value.skill_group_id
)

const handleSubmit = async () => {
  saving.value = true
  error.value = ''
  try {
    const payload = {}
    if (form.value.skill_type)    payload.skill_type     = form.value.skill_type
    if (form.value.level)         payload.level          = form.value.level
    if (form.value.atom_kind)     payload.atom_kind      = form.value.atom_kind
    if (form.value.skill_group_id) payload.skill_group_id = form.value.skill_group_id

    await platformSkillService.bulkUpdate(props.ids, payload)
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.platformSkills.failedUpdateSkills')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.125rem;
  color: #9ca3af;
  cursor: pointer;
}
.btn-close:hover { color: #374151; }

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; }

.hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
}

.field { margin-bottom: 0; flex: 1; }
.field-row { display: flex; gap: 1rem; margin-bottom: 1.25rem; }

.field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.field select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
  box-sizing: border-box;
}

.field select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.warning-msg {
  padding: 0.65rem 0.875rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  color: #92400e;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.error-msg {
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}
.btn-cancel:hover { background: #f9fafb; }

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
